/**
 * Executive AI Lead Intelligence Dashboard
 * Comprehensive executive dashboard with 20+ real-time metrics
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  TrendingUp,
  DollarSign,
  Flame,
  Target,
  MapPin,
  Globe,
  Star,
  BarChart3,
  PieChart,
  Calendar,
  Clock,
  Phone,
  Mail,
  Sparkles,
  RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Mirrors backend/server.py's PIPELINE_STAGES - the real vocabulary
// leads.status is written against (leads.pipeline_stage is not).
const PIPELINE_STAGES = ["Lead Found", "Contacted", "Interested", "Audit Booked", "Audit Delivered", "Proposal Sent", "Negotiation", "Won", "Lost"];

const AILeadIntelligenceDashboard = ({ user }) => {
  const [leads, setLeads] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  const authHeaders = useCallback(() => ({
    'Authorization': `Bearer ${user.token}`,
    'Content-Type': 'application/json'
  }), [user.token]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [leadsRes, metricsRes] = await Promise.all([
        fetch(`${API}/phase9/leads?limit=1000`, { headers: authHeaders() }),
        fetch(`${API}/phase9/leads/metrics`, { headers: authHeaders() })
      ]);

      if (leadsRes.ok) {
        const leadsData = await leadsRes.json();
        setLeads(leadsData.leads || []);
      }

      if (metricsRes.ok) {
        const metricsData = await metricsRes.json();
        setMetrics(metricsData);
      }
    } catch (error) {
      toast.error('Error fetching intelligence data');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Compute Executive Intelligence Metrics from Leads
  const computeIntelligence = useCallback(() => {
    if (!leads.length) return null;

    const scoredLeads = leads.filter(l => l.ai_lead_score !== null && l.ai_lead_score !== undefined);
    const hotLeads = scoredLeads.filter(l => l.ai_lead_score >= 80);
    const warmLeads = scoredLeads.filter(l => l.ai_lead_score >= 60 && l.ai_lead_score < 80);
    const coldLeads = scoredLeads.filter(l => l.ai_lead_score >= 40 && l.ai_lead_score < 60);
    
    // Overall AI Opportunity Score (weighted average)
    const overallScore = scoredLeads.length > 0
      ? Math.round(scoredLeads.reduce((sum, l) => sum + (l.ai_lead_score || 0), 0) / scoredLeads.length)
      : 0;

    // Revenue Opportunity
    const setupFeePerLead = 12500;
    const monthlyPerLead = 800;
    const estimatedRevenue = hotLeads.length * setupFeePerLead + (hotLeads.length + warmLeads.length) * monthlyPerLead * 12;
    const estimatedMonthlyRevenue = (hotLeads.length + warmLeads.length) * monthlyPerLead;

    // Priority Queue (Hot leads with contact info)
    const priorityQueue = hotLeads
      .filter(l => l.email || l.phone)
      .sort((a, b) => (b.ai_lead_score || 0) - (a.ai_lead_score || 0))
      .slice(0, 10);

    // Geographic Distribution
    const geoDistribution = {};
    leads.forEach(l => {
      const state = l.state || 'Unknown';
      geoDistribution[state] = (geoDistribution[state] || 0) + 1;
    });
    const topStates = Object.entries(geoDistribution)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);

    // Category Breakdown
    const categoryBreakdown = {};
    leads.forEach(l => {
      const cat = l.category || 'Med Spa';
      categoryBreakdown[cat] = (categoryBreakdown[cat] || 0) + 1;
    });

    // Website & Online Presence Scores (from lead data)
    const leadsWithWebsite = leads.filter(l => l.website).length;
    const websiteHealthScore = Math.round((leadsWithWebsite / leads.length) * 100) || 0;
    
    const leadsWithGoogle = leads.filter(l => l.google_business_url).length;
    const googlePresenceScore = Math.round((leadsWithGoogle / leads.length) * 100) || 0;
    
    const leadsWithSocial = leads.filter(l => l.instagram_url || l.facebook_url).length;
    const socialMediaScore = Math.round((leadsWithSocial / leads.length) * 100) || 0;

    // Booking Experience Score (based on rating and reviews)
    const ratedLeads = leads.filter(l => l.rating);
    const avgRating = ratedLeads.length > 0
      ? ratedLeads.reduce((sum, l) => sum + (l.rating || 0), 0) / ratedLeads.length
      : 0;
    const bookingExperienceScore = Math.round((avgRating / 5) * 100) || 0;

    // Automation Readiness Score (leads with complete data)
    const completeLeads = leads.filter(l => l.email && l.phone && l.website);
    const automationReadinessScore = Math.round((completeLeads.length / leads.length) * 100) || 0;

    // Top 20 Highest Opportunity Clinics
    const top20 = [...scoredLeads]
      .sort((a, b) => (b.ai_lead_score || 0) - (a.ai_lead_score || 0))
      .slice(0, 20);

    // Pipeline Forecast (estimated conversion)
    const hotConversionRate = 0.30; // 30% close rate for hot leads
    const warmConversionRate = 0.15; // 15% close rate for warm leads
    const projectedClients = Math.round(hotLeads.length * hotConversionRate + warmLeads.length * warmConversionRate);

    // AI Recommended Outreach Channels (based on available contact data)
    const emailAvailable = leads.filter(l => l.email).length;
    const phoneAvailable = leads.filter(l => l.phone).length;
    const recommendedChannels = [
      { channel: 'Email', count: emailAvailable, score: Math.round((emailAvailable / leads.length) * 100) },
      { channel: 'Phone', count: phoneAvailable, score: Math.round((phoneAvailable / leads.length) * 100) },
      { channel: 'Social Media', count: leadsWithSocial, score: socialMediaScore }
    ].sort((a, b) => b.score - a.score);

    // Conversion Analytics (leads by stage). Backend only ever writes
    // "New Lead"/"Proposal Sent" to leads.pipeline_stage - "New",
    // "Contacted" and "Qualified" never match anything there, same root
    // cause already fixed on the backend's GET /revenue/dashboard
    // (server.py), which switched to leads.status against PIPELINE_STAGES.
    // Mirror that fix here so the funnel actually sums to leads.length.
    const stageDistribution = PIPELINE_STAGES.reduce((acc, stage) => {
      acc[stage] = leads.filter(l => l.status === stage).length;
      return acc;
    }, {});

    return {
      overallScore,
      estimatedRevenue,
      estimatedMonthlyRevenue,
      hotLeads: hotLeads.length,
      warmLeads: warmLeads.length,
      coldLeads: coldLeads.length,
      priorityQueue,
      topStates,
      categoryBreakdown,
      websiteHealthScore,
      googlePresenceScore,
      socialMediaScore,
      bookingExperienceScore,
      automationReadinessScore,
      top20,
      projectedClients,
      recommendedChannels,
      stageDistribution,
      avgRating: avgRating.toFixed(1)
    };
  }, [leads]);

  const intelligence = computeIntelligence();

  const ExecutiveMetric = ({ icon: Icon, label, value, subValue, color = 'text-white', loading }) => (
    <article className="glass-panel">
      {loading ? (
        <>
          <Skeleton className="h-4 w-20 mb-2 bg-white/10" />
          <Skeleton className="h-8 w-32 bg-white/10" />
        </>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-3">
            <Icon className={`h-5 w-5 ${color}`} />
            <p className="text-[11px] uppercase tracking-[0.08em] text-white/60">{label}</p>
          </div>
          <strong className={`text-3xl font-semibold ${color}`}>{value}</strong>
          {subValue && <p className="text-xs text-white/60 mt-1">{subValue}</p>}
        </>
      )}
    </article>
  );

  const getBadgeClass = (label) => {
    switch (label) {
      case 'Hot': return 'bg-red-500/20 text-red-300 border-red-500/30';
      case 'Warm': return 'bg-orange-500/20 text-orange-300 border-orange-500/30';
      case 'Cold': return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      default: return 'bg-white/10 text-white/60 border-white/20';
    }
  };

  return (
    <div className="page-stack" data-testid="ai-intelligence-dashboard">
      {/* Hero Header */}
      <section className="hero-panel ceo-hero glass-panel noise-overlay">
        <div>
          <p className="eyebrow">Phase 9C — Executive AI Intelligence</p>
          <h2>AI Lead Intelligence Dashboard</h2>
          <div className="hero-stats">
            {intelligence && (
              <>
                <span>Overall Score: {intelligence.overallScore}</span>
                <span>{intelligence.hotLeads} Hot Opportunities</span>
                <span>${Math.round(intelligence.estimatedRevenue / 1000)}K Revenue Potential</span>
              </>
            )}
          </div>
        </div>
        <Button className="ghost-btn" onClick={fetchData} data-testid="refresh-intelligence-btn">
          <RefreshCw size={16} />
          Refresh
        </Button>
      </section>

      {/* Overall Metrics Grid */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ExecutiveMetric
          icon={Sparkles}
          label="Overall AI Opportunity Score"
          value={intelligence?.overallScore || 0}
          subValue="Weighted average across all leads"
          color="text-[var(--gold)]"
          loading={loading}
        />
        <ExecutiveMetric
          icon={DollarSign}
          label="Revenue Opportunity"
          value={`$${Math.round((intelligence?.estimatedRevenue || 0) / 1000)}K`}
          subValue="Setup fees + 12-month contracts"
          color="text-green-400"
          loading={loading}
        />
        <ExecutiveMetric
          icon={TrendingUp}
          label="Est. Monthly Revenue"
          value={`$${Math.round((intelligence?.estimatedMonthlyRevenue || 0) / 1000)}K`}
          subValue="Based on hot + warm leads"
          color="text-[var(--gold)]"
          loading={loading}
        />
      </section>

      {/* Lead Distribution */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ExecutiveMetric
          icon={Flame}
          label="Hot Leads"
          value={intelligence?.hotLeads || 0}
          subValue="Immediate outreach priority"
          color="text-red-400"
          loading={loading}
        />
        <ExecutiveMetric
          icon={Target}
          label="Warm Leads"
          value={intelligence?.warmLeads || 0}
          subValue="Follow-up sequence ready"
          color="text-orange-400"
          loading={loading}
        />
        <ExecutiveMetric
          icon={BarChart3}
          label="Cold Leads"
          value={intelligence?.coldLeads || 0}
          subValue="Nurture with content"
          color="text-blue-400"
          loading={loading}
        />
      </section>

      {/* Health Scores */}
      <section className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <ExecutiveMetric
          icon={Globe}
          label="Website Health Score"
          value={`${intelligence?.websiteHealthScore || 0}%`}
          color="text-white"
          loading={loading}
        />
        <ExecutiveMetric
          icon={MapPin}
          label="Google Presence Score"
          value={`${intelligence?.googlePresenceScore || 0}%`}
          color="text-white"
          loading={loading}
        />
        <ExecutiveMetric
          icon={Star}
          label="Social Media Score"
          value={`${intelligence?.socialMediaScore || 0}%`}
          color="text-white"
          loading={loading}
        />
        <ExecutiveMetric
          icon={Star}
          label="Booking Experience Score"
          value={`${intelligence?.bookingExperienceScore || 0}%`}
          subValue={`Avg ${intelligence?.avgRating || 0}★ rating`}
          color="text-white"
          loading={loading}
        />
        <ExecutiveMetric
          icon={Sparkles}
          label="Automation Readiness"
          value={`${intelligence?.automationReadinessScore || 0}%`}
          color="text-white"
          loading={loading}
        />
      </section>

      {/* AI Priority Queue */}
      <section className="glass-panel">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-white/90">AI Priority Queue</h3>
            <p className="text-sm text-white/60 mt-1">Who to contact first — ordered by opportunity score</p>
          </div>
          <Badge className="bg-[var(--gold)]/20 text-[var(--gold)]">
            {intelligence?.priorityQueue?.length || 0} Hot Leads Ready
          </Badge>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full bg-white/10" />)}
          </div>
        ) : !intelligence?.priorityQueue?.length ? (
          <div className="text-center py-12">
            <Target className="h-12 w-12 text-white/20 mx-auto mb-3" />
            <p className="text-white/60">No priority leads yet — score your leads first</p>
          </div>
        ) : (
          <div className="space-y-2">
            {intelligence.priorityQueue.map((lead, idx) => (
              <div
                key={lead.id}
                className="flex items-center justify-between p-4 border border-white/10 rounded-xl hover:bg-white/[0.02] transition-all"
                data-testid={`priority-lead-${idx}`}
              >
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 rounded-full bg-red-500/20 flex items-center justify-center border border-red-500/30">
                    <span className="text-sm font-bold text-red-300">{idx + 1}</span>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-white/90">{lead.clinic_name}</h4>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-xs text-white/60">{lead.city}, {lead.state}</span>
                      {lead.email && (
                        <span className="text-xs text-white/60 flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {lead.email}
                        </span>
                      )}
                      {lead.phone && (
                        <span className="text-xs text-white/60 flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {lead.phone}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <Badge className="bg-red-500/20 text-red-300 border-red-500/30 text-sm px-3 py-1.5 font-mono">
                  {lead.ai_lead_score}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Top 20 Highest Opportunity Clinics */}
      <section className="glass-panel">
        <h3 className="text-lg font-semibold text-white/90 mb-6">Top 20 Highest Opportunity Clinics</h3>
        {loading ? (
          <div className="space-y-2">
            {[...Array(10)].map((_, i) => <Skeleton key={i} className="h-12 w-full bg-white/10" />)}
          </div>
        ) : !intelligence?.top20?.length ? (
          <div className="text-center py-12">
            <p className="text-white/60">Score leads to see top opportunities</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {intelligence.top20.map((lead, idx) => (
              <div
                key={lead.id}
                className="flex items-center justify-between p-3 border border-white/10 rounded-lg hover:border-[var(--line-gold)] transition-all"
              >
                <div className="flex items-center gap-3">
                  <span className="text-sm font-bold text-white/40">#{idx + 1}</span>
                  <div>
                    <h5 className="text-sm font-medium text-white/90">{lead.clinic_name}</h5>
                    <p className="text-xs text-white/60">{lead.city}, {lead.state}</p>
                  </div>
                </div>
                <Badge className={`${getBadgeClass(lead.ai_lead_score_label)} text-xs`}>
                  {lead.ai_lead_score}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Geographic Distribution */}
      <section className="glass-panel">
        <h3 className="text-lg font-semibold text-white/90 mb-6">Geographic Heat Map — Top 10 States</h3>
        {loading ? (
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10 w-full bg-white/10" />)}
          </div>
        ) : !intelligence?.topStates?.length ? (
          <div className="text-center py-12">
            <p className="text-white/60">No geographic data available</p>
          </div>
        ) : (
          <div className="space-y-2">
            {intelligence.topStates.map(([state, count], idx) => {
              const percentage = Math.round((count / leads.length) * 100);
              return (
                <div key={state} className="flex items-center gap-4">
                  <span className="text-xs font-bold text-white/40 w-6">#{idx + 1}</span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-white/90">{state}</span>
                      <span className="text-xs text-white/60">{count} leads ({percentage}%)</span>
                    </div>
                    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[var(--gold)]"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Category Breakdown */}
      <section className="glass-panel">
        <h3 className="text-lg font-semibold text-white/90 mb-6">Category Breakdown</h3>
        {loading ? (
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-10 w-full bg-white/10" />)}
          </div>
        ) : !intelligence?.categoryBreakdown ? (
          <div className="text-center py-12">
            <p className="text-white/60">No category data available</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(intelligence.categoryBreakdown)
              .sort((a, b) => b[1] - a[1])
              .map(([category, count]) => (
                <div key={category} className="glass-panel">
                  <p className="text-xs text-white/60 mb-1">{category}</p>
                  <strong className="text-2xl text-white/90">{count}</strong>
                  <p className="text-xs text-white/60 mt-1">
                    {Math.round((count / leads.length) * 100)}% of total
                  </p>
                </div>
              ))}
          </div>
        )}
      </section>

      {/* Pipeline Forecast & Conversion Analytics */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="glass-panel">
          <h3 className="text-lg font-semibold text-white/90 mb-4">Pipeline Forecast</h3>
          <div className="space-y-4">
            <div>
              <p className="text-xs text-white/60 mb-2">Projected Closed Clients</p>
              <strong className="text-3xl text-[var(--gold)]">{intelligence?.projectedClients || 0}</strong>
              <p className="text-xs text-white/60 mt-1">Based on 30% hot / 15% warm conversion</p>
            </div>
            <div>
              <p className="text-xs text-white/60 mb-2">Projected Annual Revenue</p>
              <strong className="text-2xl text-green-400">
                ${Math.round(((intelligence?.projectedClients || 0) * (12500 + 800 * 12)) / 1000)}K
              </strong>
            </div>
          </div>
        </div>

        <div className="glass-panel">
          <h3 className="text-lg font-semibold text-white/90 mb-4">Conversion Analytics</h3>
          {loading ? (
            <div className="space-y-2">
              {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-8 w-full bg-white/10" />)}
            </div>
          ) : (
            <div className="space-y-3">
              {Object.entries(intelligence?.stageDistribution || {}).map(([stage, count]) => (
                <div key={stage} className="flex items-center justify-between">
                  <span className="text-sm text-white/80">{stage}</span>
                  <Badge variant="outline" className="text-xs border-white/20 text-white/70">
                    {count} leads
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* AI Recommended Outreach Channels */}
      <section className="glass-panel">
        <h3 className="text-lg font-semibold text-white/90 mb-6">AI Recommended Outreach Channels</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {intelligence?.recommendedChannels?.map((channel) => (
            <div key={channel.channel} className="glass-panel">
              <div className="flex items-center gap-2 mb-3">
                {channel.channel === 'Email' && <Mail className="h-5 w-5 text-[var(--gold)]" />}
                {channel.channel === 'Phone' && <Phone className="h-5 w-5 text-[var(--gold)]" />}
                {channel.channel === 'Social Media' && <Star className="h-5 w-5 text-[var(--gold)]" />}
                <p className="text-sm font-semibold text-white/90">{channel.channel}</p>
              </div>
              <strong className="text-2xl text-white/90">{channel.count}</strong>
              <p className="text-xs text-white/60 mt-1">{channel.score}% coverage</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default AILeadIntelligenceDashboard;
